import { Component, ElementRef, EventEmitter, inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PasInputFieldComponent,
  PasInputNumberComponent,
  PasLayoutSidebarHolderComponent,
  PasSidebarHandleService,
  PasSpinnerComponent,
} from '@porsche-aftersales/frontend-toolbox';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { cloneDeep, isEqual } from 'lodash-es';

import { hasAfterSalesSuppliersAndRatesRight } from '../../../services/after-sales/after-sales-functions';
import { AfterSalesStore } from '../../../services/after-sales/after-sales-store.service';
import { AfterSalesService } from '../../../services/after-sales/after-sales.service';
import { AGGridExcelService } from '../../../services/ag-grid/excel-service';
import { AGGridFormatterService } from '../../../services/ag-grid/formatter-service';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { UserService } from '../../../services/user-service/user.service';
import { AgGridService } from '../../../shared/ag-grid/ag-grid.service';
import { GenericModalContentComponent } from '../../../shared/generic-modal-content/generic-modal-content.component';
import { ErrorCostRateModel } from '../model/after-sales-error-cost-rate-model';
import { ErrorCostRateButtonCellRenderer } from '../renderer/error-cost-rate-button-cell-renderer.component';

@Component({
  selector: 'app-after-sales-error-cost-rate',
  styleUrls: ['./after-sales-error-cost-rate.component.scss'],
  templateUrl: './after-sales-error-cost-rate.component.html',
  imports: [
    PasSpinnerComponent,
    AgGridAngular,
    PasLayoutSidebarHolderComponent,
    GenericModalContentComponent,
    ReactiveFormsModule,
    PasInputFieldComponent,
    TranslatePipe,
    PasInputNumberComponent,
  ],
})
export class AfterSalesErrorCostRateComponent implements OnInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly agGridService = inject(AgGridService);
  private readonly agGridFormatterService = inject(AGGridFormatterService);
  private readonly agGridExcelService = inject(AGGridExcelService);
  private readonly afterSalesStore = inject(AfterSalesStore);
  private readonly userService = inject(UserService);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly modalService = inject(NgbModal);
  private readonly afterSalesService = inject(AfterSalesService);

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output('onBtnAfterSalesErrorCostRateCancel') public onBtnAfterSalesErrorCostRateCancel = new EventEmitter();

  @ViewChild('ErrorCostRateDialogTemplate')
  public errorCostRateDialogTemplate: ElementRef;
  public errorCostRateDialog: NgbModalRef;
  public errorCostRateDialogForm: UntypedFormGroup;
  public acceptanceAllowed = false;
  public formValueChanged = false;
  public initialFormValue: any;
  public decimalSeparator: ',' | '.' = '.';
  public userLanguage: '' | 'DE';
  public spinnerActive = false;
  public validationMessages: { error: string; message: string }[];
  private errorCostRateUpdate = false;

  public gridOptions: GridOptions;
  public columnDefs: any;
  public rowData: any;
  public defaultColDef: any;
  public excelStyles: any;
  public multiSortKey: any;
  public gridApi: any;
  public frameworkComponents: any;
  public signatureNotValidList: string[] = [];
  private tmpParam: any;
  public errorCostRatesOverviewData: any[] = [];

  public constructor() {
    this.frameworkComponents = {
      errorCostRateButtonCellRenderer: ErrorCostRateButtonCellRenderer,
    };
  }

  public async ngOnInit(): Promise<void> {
    this.decimalSeparator = this.userService.userLanguage.startsWith('en') ? '.' : ',';
    this.gridOptions = {
      rowHeight: 35,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      getLocaleText: this.agGridService.translate(this.userService, this.translateService),
      overlayComponentParams: {
        noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
      },
    };

    this.defaultColDef = {
      resizable: true,
      sortable: true,
      unSortIcon: true,
      floatingFilter: true,
    };

    this.translateService.get('AFTER_SALES_ERROR_COST_RATE_TAB').subscribe(() => {
      this.columnDefs = [
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_ERROR_COST_ERROR_ID'),
          field: 'errorCodeId',
          filter: 'agTextColumnFilter',
          cellStyle: { textAlign: 'right' },
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_ERROR_COST_ERROR_DESCRIPTION'),
          field: 'errorCodeDescription',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_ERROR_COST_ERROR_CLASS'),
          field: 'errorCodeClass',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_ERROR_COST_RATE'),
          field: 'costRate',
          valueFormatter: this.agGridFormatterService.currencyFormatterDefault(this.userService),
          filter: 'agTextColumnFilter',
          cellStyle: { textAlign: 'right' },
          cellClass: 'currencyFormat',
          filterParams: {
            textFormatter: val => (val === null ? null : val.toString().replace(',', '.')),
          },
        },
        {
          colId: 'buttonColumn',
          headerName: '',
          headerClass: ['ag-header-last-cell'],
          minWidth: 100,
          maxWidth: 100,
          filter: false,
          cellRenderer: 'errorCostRateButtonCellRenderer',
          cellRendererParams: {
            onEditClicked: this.onEditErrorCostRateBtn.bind(this),
          },
          field: 'value',
          menuTabs: [],
          unSortIcon: false,
        },
      ];
    });

    this.initValidationMessages();
    this.initErrorCostRateDialogFormFields();
    this.initialFormValue = cloneDeep(this.errorCostRateDialogForm.value);
    this.onChanges();

    this.excelStyles = this.agGridExcelService.getExcelStyles(this.translateService);
    this.multiSortKey = 'ctrl';
  }

  public onFirstDataRendered(params): void {
    params.api.sizeColumnsToFit();
  }

  public onGridSizeChanged(params): void {
    // eslint-disable-next-line no-restricted-globals
    const gridWidth = (document.querySelector('#grid-wrapper') as any).offsetWidth;
    const columnsToShow = [];
    const columnsToHide = [];
    let totalColsWidth = 0;
    const allColumns = params.api.getColumns();
    for (const column of allColumns) {
      totalColsWidth += column.getMinWidth();
      if (totalColsWidth > gridWidth) {
        columnsToHide.push(column.colId);
      } else {
        columnsToShow.push(column.colId);
      }
    }
    params.api.setColumnsVisible(columnsToShow, true);
    params.api.setColumnsVisible(columnsToHide, false);
    params.api.sizeColumnsToFit();
  }

  public enableSidebar(): void {
    if (this.userHasAnyRight()) {
      this.sidebarHandleService.enableSidebarForView();
    }
  }

  public async onGridReady(event: GridReadyEvent): Promise<void> {
    this.enableSidebar();
    this.tmpParam = event;
    this.gridApi = event.api;
    this.gridApi.setGridOption('headerHeight', 32);

    event.api.applyColumnState({
      state: [{ colId: 'errorCodeId', sort: 'asc' }],
    });

    this.errorCostRatesOverviewData = await this.afterSalesStore.getErrorCostRateTableData();
    event.api.setGridOption('rowData', this.errorCostRatesOverviewData);
    this.checkValidationOfSignatures(this.errorCostRatesOverviewData);
  }

  public refreshTable(): void {
    this.onGridReady(this.tmpParam);
  }

  private checkValidationOfSignatures(data: ErrorCostRateModel[]): void {
    data.forEach(item => {
      if (!item.signatureValid) {
        this.signatureNotValidList.push(item.errorCodeId);
      }
    });

    if (this.signatureNotValidList.length > 0) {
      this.translateService
        .get(['AUTHENTICITY_ERROR_COST_RATE_FAILED_TITLE', 'AUTHENTICITY_ERROR_COST_RATE_FAILED'])
        .subscribe((translate: { [key: string]: any }) => {
          const title: string = translate.AUTHENTICITY_ERROR_COST_RATE_FAILED_TITLE;
          const message: string = this.translateService.instant('AUTHENTICITY_ERROR_COST_RATE_FAILED', {
            0:
              this.signatureNotValidList.length > 11
                ? `${this.signatureNotValidList.filter((item, index) => index < 10).join(', ')}...`
                : this.signatureNotValidList.join(', '),
          });

          this.notificationServiceWrapper.addErrorNotification(title, message);
        });
    }
  }

  private initErrorCostRateDialogFormFields(): void {
    this.errorCostRateDialogForm = new UntypedFormGroup({
      errorCodeId: new UntypedFormControl({ value: '', disabled: false }, { validators: [Validators.required] }),
      errorCodeDescription: new UntypedFormControl(
        { value: '', disabled: false },
        { validators: [Validators.required] },
      ),
      errorCodeClass: new UntypedFormControl({ value: '', disabled: false }, { validators: [Validators.required] }),
      costRate: new UntypedFormControl({ value: '', disabled: false }, { validators: [Validators.required] }),
    });
  }

  private onChanges(): void {
    this.errorCostRateDialogForm.valueChanges.subscribe(newFormValue => {
      this.formValueChanged = this.areFormFieldsChanged(this.initialFormValue, newFormValue);
    });
  }

  private areFormFieldsChanged(initialFormValue, newFormValue): boolean {
    let formChanged = false;
    Object.keys(initialFormValue).forEach((key: string) => {
      const initialFieldValue = initialFormValue[key];
      const newFieldValue = newFormValue[key];
      if (key === 'errorCost' || key === 'costRate') {
        if (!this.areNumericValuesEqual(initialFieldValue, newFieldValue)) {
          formChanged = true;
        }
      } else {
        if (!isEqual(initialFieldValue, newFieldValue) && !this.stringsNullOrEmpty(initialFieldValue, newFieldValue)) {
          formChanged = true;
        }
      }
    });
    return formChanged;
  }

  private areNumericValuesEqual(initValue, newValue): boolean {
    return Number(initValue) === Number(newValue);
  }

  private stringsNullOrEmpty(initValue, newValue): boolean {
    return this.isStringNullOrEmpty(initValue) && this.isStringNullOrEmpty(newValue);
  }

  private isStringNullOrEmpty(inputString): boolean {
    return !inputString || inputString.length === 0;
  }

  private initValidationMessages(): void {
    this.validationMessages = [
      {
        error: 'required',
        message: this.translateService.instant('VALIDATION_INPUT_REQUIRED'),
      },
    ];
  }

  public getContextMenuItems(): any[] {
    return [];
  }

  public onBtnCancel(): void {
    this.onBtnAfterSalesErrorCostRateCancel.emit();
  }

  public ngOnDestroy(): void {
    this.sidebarHandleService.disableSidebarForView();
  }

  public canUserCreateErrorCostRate(): boolean {
    return hasAfterSalesSuppliersAndRatesRight(this.userService.getRights());
  }

  public userHasAnyRight(): boolean {
    return this.userService.getRights().length > 0;
  }

  public onNewErrorCostRateBtn(): void {
    this.errorCostRateUpdate = false;
    this.openErrorCostRateDialog();
  }

  public onEditErrorCostRateBtn(event: any): void {
    this.errorCostRateUpdate = true;
    this.openErrorCostRateDialog(event.rowData);
  }

  public openErrorCostRateDialog(rowData?: any): void {
    this.errorCostRateDialog = this.modalService.open(this.errorCostRateDialogTemplate, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
    });

    if (rowData == undefined) {
      this.errorCostRateDialogForm.get('errorCodeId').enable();
      this.errorCostRateDialogForm.get('errorCodeClass').enable();
      this.errorCostRateDialogForm.get('errorCodeDescription').enable();
    } else {
      this.errorCostRateDialogForm.patchValue({
        errorCodeId: rowData.errorCodeId,
        errorCodeClass: rowData.errorCodeClass,
        errorCodeDescription: rowData.errorCodeDescription,
        costRate: rowData.costRate,
      });

      this.errorCostRateDialogForm.get('errorCodeId').disable();
      this.errorCostRateDialogForm.get('errorCodeClass').disable();
      this.errorCostRateDialogForm.get('errorCodeDescription').disable();

      this.initialFormValue = cloneDeep(this.errorCostRateDialogForm.value);
    }
  }

  public onCloseErrorCostRateDialog(): void {
    this.resetErrorCostRateForm();
    this.errorCostRateDialog.close();
  }

  public onAcceptErrorCostRateDialog(): void {
    this.spinnerActive = true;
    this.disableErrorCostRateForm(true);

    this.afterSalesService.saveErrorCostRate(this.createOrUpdateErrorCostRate(), this.errorCostRateUpdate).subscribe({
      next: errorCostRateSaved => {
        const title: string = this.translateService.instant('SAVE_ERROR_COST_RATE_SUCCESS_TITLE');
        const content: string = this.translateService.instant('SAVE_ERROR_COST_RATE_SUCCESS_MESSAGE', {
          0: `${errorCostRateSaved.errorCodeId} ${errorCostRateSaved.errorCodeDescription}`,
        });
        this.notificationServiceWrapper.addSuccessNotification(title, content);
        this.refreshTable();
      },
      error: err => {
        const title: string = this.translateService.instant('SAVE_ERROR_COST_RATE_ERROR_TITLE');
        const content =
          err.error.errorCode === 13_006 && !this.errorCostRateUpdate
            ? this.translateService.instant('SAVE_ERROR_COST_RATE_ERROR_DUPLICATE_MESSAGE', {
                0: this.errorCostRateDialogForm.value.errorCodeId,
              })
            : this.translateService.instant('SAVE_ERROR_COST_RATE_ERROR_MESSAGE', {
                0: `${this.errorCostRateDialogForm.value.errorCodeId} ${this.errorCostRateDialogForm.value.errorCodeDescription}`,
              });
        this.notificationServiceWrapper.addErrorNotification(title, content);
        this.disableErrorCostRateForm(false);
        this.spinnerActive = false;
        this.onCloseErrorCostRateDialog();
      },
      complete: () => {
        this.disableErrorCostRateForm(false);
        this.spinnerActive = false;
        this.onCloseErrorCostRateDialog();
      },
    });
  }

  private createOrUpdateErrorCostRate(): ErrorCostRateModel {
    const errorCostRateDialogData = this.errorCostRateDialogForm.value;

    const errorCostRateModel = new ErrorCostRateModel();
    errorCostRateModel.errorCodeId = errorCostRateDialogData.errorCodeId;
    errorCostRateModel.errorCodeClass = errorCostRateDialogData.errorCodeClass;
    errorCostRateModel.errorCodeDescription = errorCostRateDialogData.errorCodeDescription;
    errorCostRateModel.costRate = errorCostRateDialogData.costRate;
    return errorCostRateModel;
  }

  private resetErrorCostRateForm(): void {
    this.errorCostRateDialogForm.setValue({
      errorCodeId: '',
      errorCodeClass: '',
      errorCodeDescription: '',
      costRate: '',
    });
  }

  public isAcceptanceAllowed(): boolean {
    return (
      this.errorCostRateDialogForm &&
      this.errorCostRateDialogForm.dirty &&
      this.errorCostRateDialogForm.valid &&
      this.formValueChanged
    );
  }

  public disableErrorCostRateForm(disableFlag: boolean): any {
    return disableFlag === true ? this.errorCostRateDialogForm.disable() : this.errorCostRateDialogForm.enable();
  }

  public forceUppercaseConditionally(formControlName, event): any {
    this.errorCostRateDialogForm.get(formControlName).setValue(event.target.value.toUpperCase());
  }

  public onlyNumbersAlowed(event): any {
    return (event.charCode != 8 && event.charCode == 0) || (event.charCode >= 48 && event.charCode <= 57);
  }
}
